import React, { useRef, useState, useEffect } from 'react';
import {
  Box,
  FormControlLabel,
  FormLabel,
  FormControl,
  Typography,
  CircularProgress,
  IconButton,
  InputAdornment,
  Checkbox,
  useTheme,
  FormHelperText,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import {
  GlassTextField,
  GlassButton,
  ButtonTertiary,
  SectionHeader,
  BodyText,
  GlassColors,
} from '../glassmorphism/GlassmorphismStyles';
import RailStepper from '../../components/RailStepper/RailStepper';

interface PassedProps {
  onSubmit: (
    email: string,
    password: string,
    firstname: string,
    lastname: string,
    location?: string,
  ) => void;
  isLoading: boolean;
  isDuplicatedEmailError: boolean;
  selectedRole: 'handyman' | 'customer';
  isHandyman: boolean;
  onBack: () => void;
}

const SignUpForm = (props: PassedProps) => {
  const [emailError, setEmailError] = React.useState(false);
  const [emailErrorMessage, setEmailErrorMessage] = React.useState('');
  const [passwordError, setPasswordError] = React.useState(false);
  const [passwordErrorMessage, setPasswordErrorMessage] = React.useState('');
  const [agreementError, setAgreementError] = React.useState(false);
  const [agreementErrorMessage, setAgreementErrorMessage] = React.useState('');
  const [firstNameError, setFirstNameError] = React.useState(false);
  const [firstNameErrorMessage, setFirstNameErrorMessage] = React.useState('');
  const [lastNameError, setLastNameError] = React.useState(false);
  const [lastNameErrorMessage, setLastNameErrorMessage] = React.useState('');
  const [selectedAddress, setSelectedAddress] = useState<string>('');
  const autocompleteElementRef = useRef<HTMLElement | null>(null);
  const [locationError, setLocationError] = React.useState(false);
  const [locationErrorMessage, setLocationErrorMessage] = React.useState('');
  const [showPassword, setShowPassword] = React.useState(false);

  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Initialize Places API when component mounts
  useEffect(() => {
    if (props.selectedRole === 'handyman' && window.google?.maps?.places) {
      const locationInput = document.getElementById(
        'location',
      ) as HTMLInputElement;
      if (locationInput && !autocompleteElementRef.current) {
        // Use the new Places API
        const autocomplete = new window.google.maps.places.Autocomplete(
          locationInput,
          {
            types: ['address'],
            //componentRestrictions: { country: 'gb' }
          },
        );

        autocomplete.addListener('place_changed', () => {
          const place = autocomplete.getPlace();
          if (place?.formatted_address) {
            setSelectedAddress(place.formatted_address);
            setLocationError(false);
            setLocationErrorMessage('');
          }
        });

        autocompleteElementRef.current = locationInput;
      }
    }
  }, [props.selectedRole]);

  React.useEffect(() => {
    if (props.isDuplicatedEmailError) {
      setEmailError(true);
      setEmailErrorMessage('Email already exists.');
    }
  }, [props.isDuplicatedEmailError]);

  const validateInputs = () => {
    const email = document.getElementById('email') as HTMLInputElement;
    const password = document.getElementById('password') as HTMLInputElement;
    const firstname = document.getElementById('firstname') as HTMLInputElement;
    const lastname = document.getElementById('lastname') as HTMLInputElement;
    const agreement = document.getElementById('agreement') as HTMLInputElement;
    const locationInput = document.getElementById(
      'location',
    ) as HTMLInputElement;

    let isValid = true;

    if (!email.value || !/\S+@\S+\.\S+/.test(email.value)) {
      setEmailError(true);
      setEmailErrorMessage('Please enter a valid email address.');
      isValid = false;
    } else {
      setEmailError(false);
      setEmailErrorMessage('');
    }

    if (!password.value || password.value.length < 6) {
      setPasswordError(true);
      setPasswordErrorMessage('Password must be at least 6 characters long.');
      isValid = false;
    } else {
      setPasswordError(false);
      setPasswordErrorMessage('');
    }

    if (!firstname.value || firstname.value.length < 1) {
      setFirstNameError(true);
      setFirstNameErrorMessage('First name is required.');
      isValid = false;
    } else {
      setFirstNameError(false);
      setFirstNameErrorMessage('');
    }

    if (!lastname.value || lastname.value.length < 1) {
      setLastNameError(true);
      setLastNameErrorMessage('Last name is required.');
      isValid = false;
    } else {
      setLastNameError(false);
      setLastNameErrorMessage('');
    }

    if (!agreement?.checked) {
      setAgreementError(true);
      setAgreementErrorMessage(
        'You must agree to the Terms and Privacy Policy.',
      );
      isValid = false;
    } else {
      setAgreementError(false);
      setAgreementErrorMessage('');
    }

    if (props.selectedRole === 'handyman') {
      if (
        !locationInput ||
        !locationInput.value ||
        locationInput.value.length < 1
      ) {
        setLocationError(true);
        setLocationErrorMessage('Location is required.');
        isValid = false;
      } else {
        setLocationError(false);
        setLocationErrorMessage('');
      }
    }

    return isValid;
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const isValid = validateInputs();

    if (!isValid) {
      return;
    }

    const data = new FormData(event.currentTarget);
    props.onSubmit(
      data.get('email') as string,
      data.get('password') as string,
      data.get('firstname') as string,
      data.get('lastname') as string,
      selectedAddress || undefined,
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box
        sx={{
          textAlign: 'center',
          maxWidth: { xs: '208px', sm: 'none' },
          margin: '0 auto',
        }}
      >
        <SectionHeader
          variant='h4'
          sx={{
            fontSize: { xs: '20px', sm: '24px' },
            lineHeight: { xs: '160%', sm: '133%' },
            letterSpacing: { xs: '.15px', sm: 0 },
            fontFamily: 'Montserrat, sans-serif',
            fontWeight: { xs: 800, sm: 700 },
            filter: 'drop-shadow(0px -0.25px 0.4px #0000008C)',
            color: isDark
              ? GlassColors.dark.accent
              : GlassColors.light.secondary,
            mb: '8px',
          }}
        >
          {props.selectedRole === 'handyman'
            ? 'Create Your Provider Account'
            : 'Create Your Account'}
        </SectionHeader>
        <BodyText
          sx={{
            fontSize: { xs: '14px', sm: '16px' },
            fontWeight: 700,
            lineHeight: { xs: '157%', sm: '175%' },
            letterSpacing: { xs: '.1px', sm: '.15px' },
            mx: 'auto',
          }}
        >
          {props.selectedRole === 'handyman'
            ? `Great choice! Let's get you set up to start earning`
            : `Let's get started. A free account is all you need to connect with qualified professionals`}
        </BodyText>
      </Box>
      <Box
        component='form'
        onSubmit={handleSubmit}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          width: '100%',
        }}
      >
        <Grid container spacing={1}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth>
              <FormLabel
                htmlFor='firstname'
                sx={{
                  color: isDark
                    ? GlassColors.dark.textSecondary
                    : GlassColors.textPrimary,
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '14px',
                  fontWeight: 400,
                }}
              >
                First name*
              </FormLabel>
              <GlassTextField
                autoComplete='firstname'
                name='firstname'
                required
                fullWidth
                id='firstname'
                placeholder=''
                error={firstNameError}
                helperText={firstNameErrorMessage}
              />
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth>
              <FormLabel
                htmlFor='lastname'
                sx={{
                  color: isDark
                    ? GlassColors.dark.textSecondary
                    : GlassColors.textPrimary,
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '14px',
                  fontWeight: 400,
                }}
              >
                Last name*
              </FormLabel>
              <GlassTextField
                autoComplete='lastname'
                name='lastname'
                required
                fullWidth
                id='lastname'
                placeholder=''
                error={lastNameError}
                helperText={lastNameErrorMessage}
              />
            </FormControl>
          </Grid>
        </Grid>

        <FormControl>
          <FormLabel
            htmlFor='email'
            sx={{
              color: isDark
                ? GlassColors.dark.textSecondary
                : GlassColors.textPrimary,
              fontFamily: 'Roboto, sans-serif',
              fontSize: '14px',
              fontWeight: 400,
            }}
          >
            Email*
          </FormLabel>
          <GlassTextField
            required
            fullWidth
            id='email'
            placeholder=''
            name='email'
            autoComplete='email'
            error={emailError}
            helperText={emailErrorMessage}
          />
        </FormControl>

        {props.selectedRole === 'handyman' && (
          <FormControl>
            <FormLabel
              htmlFor='location'
              sx={{
                color: isDark
                  ? GlassColors.dark.textSecondary
                  : GlassColors.textPrimary,
                fontFamily: 'Roboto, sans-serif',
                fontSize: '14px',
                fontWeight: 400,
              }}
            >
              Enter your service location*
            </FormLabel>
            <GlassTextField
              fullWidth
              name='location'
              id='location'
              placeholder=''
              error={locationError}
              helperText={locationErrorMessage}
              value={selectedAddress}
              onChange={(e) => setSelectedAddress(e.target.value)}
            />
          </FormControl>
        )}

        <FormControl>
          <FormLabel
            htmlFor='password'
            sx={{
              color: isDark
                ? GlassColors.dark.textSecondary
                : GlassColors.textPrimary,
              fontFamily: 'Roboto, sans-serif',
              fontSize: '14px',
              fontWeight: 400,
            }}
          >
            Password*
          </FormLabel>
          <GlassTextField
            required
            fullWidth
            name='password'
            placeholder=''
            type={showPassword ? 'text' : 'password'}
            id='password'
            autoComplete='new-password'
            error={passwordError}
            helperText={passwordErrorMessage}
            InputProps={{
              endAdornment: (
                <InputAdornment sx={{ display: 'none' }} position='end'>
                  <IconButton
                    onClick={handleTogglePasswordVisibility}
                    edge='end'
                    aria-label='toggle password visibility'
                    sx={{
                      color: '#585858',
                      '&:hover': {
                        color: '#1A1A1A',
                      },
                    }}
                  >
                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </FormControl>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
          }}
        >
          <FormControlLabel
            sx={{ alignItems: 'start' }}
            control={
              <Checkbox
                value='allowExtraEmails'
                sx={{
                  color: 'rgba(26, 26, 26, 1)',
                  '&.Mui-checked': {
                    color: 'rgba(0, 92, 173, 1)',
                  },
                  '& .MuiSvgIcon-root': { fontSize: 28 },
                }}
              />
            }
            label={
              <Typography
                sx={{
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '16px',
                  fontWeight: 700,
                  lineHeight: '175%',
                  letterSpacing: '.15px',
                  pt: '9px',
                  '&&': {
                    color: isDark ? GlassColors.dark.textPrimary : '#393939',
                  },
                }}
              >
                Get tips and special offers
              </Typography>
            }
          />
          <FormControl
            error={agreementError}
            sx={{ width: { xs: '100%', tablet: '50%' } }}
          >
            <FormControlLabel
              sx={{
                mr: 0,
                alignItems: 'start',
              }}
              control={
                <Checkbox
                  id='agreement'
                  value='agreement'
                  sx={{
                    color: 'rgba(26, 26, 26, 1)',
                    '&.Mui-checked': { color: 'rgba(0, 92, 173, 1)' },
                    '& .MuiSvgIcon-root': { fontSize: 28 },
                  }}
                />
              }
              label={
                <Typography
                  sx={{
                    fontFamily: 'Roboto, sans-serif',
                    fontSize: '16px',
                    fontWeight: 700,
                    lineHeight: '175%',
                    letterSpacing: '.15px',
                    pt: '9px',
                    '&&': {
                      color: isDark ? GlassColors.dark.textPrimary : '#393939',
                    },
                    '& a': {
                      color: isDark
                        ? GlassColors.dark.accent
                        : GlassColors.light.secondary,
                    },
                    '& a:hover': { textDecoration: 'none' },
                  }}
                >
                  I agree to the{' '}
                  <a
                    href='https://womapp.uk/terms-of-service'
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    Terms of Service,
                  </a>{' '}
                  <a
                    href='https://womapp.uk/platform-fees'
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    Platform Fee policy,
                  </a>{' '}
                  <a
                    href='https://womapp.uk/refund-policy'
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    Refund Policy,
                  </a>{' '}
                  and{' '}
                  <a
                    href='https://womapp.uk/privacy-policy'
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    Privacy Policy
                  </a>
                  *
                </Typography>
              }
            />

            {agreementError && (
              <FormHelperText>{agreementErrorMessage}</FormHelperText>
            )}
          </FormControl>
        </Box>
        <RailStepper isProviderFlow={props.isHandyman} activeIndex={1} />
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 2,
            mt: 1,
          }}
        >
          <ButtonTertiary onClick={props.onBack} disabled={props.isLoading}>
            Back
          </ButtonTertiary>
          <GlassButton
            type='submit'
            onClick={validateInputs}
            disabled={props.isLoading}
            sx={{ fontSize: '12px' }}
          >
            {props.isLoading ? (
              <CircularProgress size={20} style={{ color: 'white' }} />
            ) : props.isHandyman ? (
              'Next'
            ) : (
              'Create account'
            )}
          </GlassButton>
        </Box>
      </Box>

      {/* 
      <Typography
        sx={{
          textAlign: 'center',
          fontFamily: 'Roboto, sans-serif',
          fontSize: '14px',
          color: '#393939',
          mt: 3,
        }}
      >
        Already have an account?{' '}
        <Link
          href='sign-in'
          sx={{
            color: '#005CAD',
            textDecoration: 'none',
            fontWeight: 500,
            '&:hover': {
              textDecoration: 'underline',
              color: '#00E8E7',
            },
          }}
        >
          Sign in
        </Link>
      </Typography>
      */}
    </Box>
  );
};

export default SignUpForm;
